import { createContext, useContext, useMemo, type PropsWithChildren } from "react";

import { useTouchpoints } from "hooks/api/use-touchpoints";
import { useCampaignJobPolling } from "hooks/use-campaign-job-polling";

import { TouchpointDTOBatchJobExecutionStatus } from "generated/schemas";

type CampaignJobPollingContextValue = {
  startPolling: () => void;
};

const CampaignJobPollingContext = createContext<CampaignJobPollingContextValue | null>(null);

export const CampaignJobPollingProvider = ({ children }: PropsWithChildren) => {
  const { data } = useTouchpoints({});

  const hasAnyRunningTouchpoint = useMemo(
    () =>
      data?.items?.some(
        (item) => item.batchJobExecutionStatus === TouchpointDTOBatchJobExecutionStatus.RUNNING,
      ) ?? false,
    [data?.items],
  );

  const { startPolling } = useCampaignJobPolling(hasAnyRunningTouchpoint);

  const value = useMemo(() => ({ startPolling }), [startPolling]);

  return (
    <CampaignJobPollingContext.Provider value={value}>{children}</CampaignJobPollingContext.Provider>
  );
};

export const useCampaignJobPollingContext = (): CampaignJobPollingContextValue => {
  const context = useContext(CampaignJobPollingContext);

  if (!context) {
    throw new Error("useCampaignJobPollingContext must be used within CampaignJobPollingProvider");
  }

  return context;
};
